import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TextField, Button, Autocomplete, Grid, Box } from '@mui/material';

const listBoxSx = {
  fontSize: '0.875rem',
  maxHeight: 250,
  overflowY: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  '& .MuiAutocomplete-option': {
    '&[aria-selected="true"]:not(:hover)': {
      backgroundColor: '#fff !important',
      color: '#000 !important',
    },
    '&[aria-selected="true"]:hover, &[aria-selected="true"].Mui-focusVisible': {
      backgroundColor: '#fcf1ad !important',
      color: '#000 !important',
    },
    '&:not([aria-selected="true"]):hover, &:not([aria-selected="true"]).Mui-focusVisible': {
      backgroundColor: '#fcf1ad !important',
      color: '#000 !important',
    },
    '&:not([aria-selected="true"])': {
      backgroundColor: 'transparent',
      color: '#000',
    },
  },
};

const prioridades = ['ALTA', 'MEDIA', 'BAJA'] as const;
const usosAula = ['MIXTA', 'SOLO_ONLINE', 'SOLO_PRESENCIAL'] as const;
const internetPerfiles = ['SIN_INTERNET', 'BASICO', 'ESTABLE'] as const;

const schema = z.object({
  nombre: z.string().min(1).max(100),
  capacidad: z.number().int().min(1),
  capacidad_cat: z.enum(['S', 'M', 'L']),
  planta: z.number().int().optional(),
  zona: z.string().max(64).optional(),
  /** ← NUEVO: prioridad requerida en el form */
  prioridad: z.enum(prioridades),
  usoAula: z.enum(usosAula),
  internetPerfil: z.enum(internetPerfiles),
  internetObservaciones: z.string().max(255).optional(),
});

export type AulaFormData = z.infer<typeof schema>;

const categorias = ['S', 'M', 'L'] as const;
const plantas = [0, 1, 2, 3, 4, 5];
const zonas = ['A', 'B', 'C', 'D'];

export function AulaForm({
  defaultValues,
  onSubmit,
  submitText = 'Guardar',
}: {
  defaultValues?: Partial<AulaFormData>;
  onSubmit: (data: AulaFormData) => void | Promise<void>;
  submitText?: string;
}) {
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isSubmitting },
  } = useForm<AulaFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: '',
      capacidad: 1,
      capacidad_cat: 'S',
      planta: undefined,
      zona: undefined,
      prioridad: 'MEDIA', // ← default coherente con el back
      usoAula: 'MIXTA',
      internetPerfil: 'BASICO',
      internetObservaciones: undefined,
      ...defaultValues,
    },
  });

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <TextField
        label="Nombre"
        {...register('nombre')}
        error={!!errors.nombre}
        helperText={errors.nombre?.message}
        fullWidth
      />

      {/* Fila: Capacidad + Categoría */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Capacidad"
            type="number"
            {...register('capacidad', { valueAsNumber: true })}
            error={!!errors.capacidad}
            helperText={errors.capacidad?.message}
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="capacidad_cat"
            control={control}
            render={({ field }) => (
              <Autocomplete<(typeof categorias)[number], false, false, false>
                options={categorias as any}
                value={field.value ?? 'S'}
                onChange={(_, value) => field.onChange(value ?? 'S')}
                ListboxProps={{ sx: listBoxSx }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categoría"
                    error={!!errors.capacidad_cat}
                    helperText={errors.capacidad_cat?.message}
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Fila: Prioridad */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="prioridad"
            control={control}
            render={({ field }) => (
              <Autocomplete<(typeof prioridades)[number], false, false, false>
                options={prioridades as any}
                value={field.value ?? 'MEDIA'}
                onChange={(_, value) => field.onChange(value ?? 'MEDIA')}
                ListboxProps={{ sx: listBoxSx }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Prioridad"
                    error={!!errors.prioridad}
                    helperText={errors.prioridad?.message}
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Fila: Planta + Zona */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Controller
            name="planta"
            control={control}
            render={({ field }) => (
              <Autocomplete<number, false, false, false>
                options={plantas}
                value={field.value ?? null}
                onChange={(_, value) => field.onChange(value ?? undefined)}
                ListboxProps={{ sx: listBoxSx }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Planta"
                    error={!!errors.planta}
                    helperText={errors.planta?.message}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="zona"
            control={control}
            render={({ field }) => (
              // Generics: <TValue, TMultiple, TDisableClearable, TFreeSolo>
              <Autocomplete<string, false, false, true>
                freeSolo
                options={zonas}
                value={field.value ?? ''}
                onChange={(_, value) => field.onChange((value ?? '').trim() || undefined)}
                ListboxProps={{ sx: listBoxSx }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Zona"
                    error={!!errors.zona}
                    helperText={errors.zona?.message}
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Fila: Uso aula + Internet */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Controller
            name="usoAula"
            control={control}
            render={({ field }) => (
              <Autocomplete<(typeof usosAula)[number], false, false, false>
                options={usosAula as any}
                value={field.value ?? 'MIXTA'}
                onChange={(_, value) => field.onChange(value ?? 'MIXTA')}
                ListboxProps={{ sx: listBoxSx }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Uso aula"
                    error={!!errors.usoAula}
                    helperText={errors.usoAula?.message}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="internetPerfil"
            control={control}
            render={({ field }) => (
              <Autocomplete<(typeof internetPerfiles)[number], false, false, false>
                options={internetPerfiles as any}
                value={field.value ?? 'BASICO'}
                onChange={(_, value) => field.onChange(value ?? 'BASICO')}
                ListboxProps={{ sx: listBoxSx }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Perfil internet"
                    error={!!errors.internetPerfil}
                    helperText={errors.internetPerfil?.message}
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>

      <TextField
        label="Observaciones internet"
        {...register('internetObservaciones')}
        error={!!errors.internetObservaciones}
        helperText={errors.internetObservaciones?.message}
        fullWidth
      />

      <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
        {submitText}
      </Button>
    </Box>
  );
}
